/**
 * Pure helpers for web-push notification navigation (N1 destination contract,
 * task W3). The service worker's notificationclick handler and the page-side
 * hooks (useSwNotificationNavigate, useNrefBootResolve) both import from this
 * file - keep it free of DOM/SW-specific APIs and React, same convention as
 * sw-messages.ts / push-suppression.ts.
 *
 * Transition semantics (dual-read): the SW only SHIPS flattened refs - warm
 * taps postMessage them, cold taps pack them into /notifications?nref= - and
 * the PAGE resolves them via resolveNotificationTarget. The resolver must NOT
 * be imported here or anywhere in the SW bundle: it drags routes/screens code
 * into the worker and would fork destination logic across SW versions. The
 * vendored registry JSON (data only, no code) IS import-safe and is used below
 * to prune the cold-start URL to the fields the resolver actually reads.
 */

import destinationsRegistry from '@lib/notifications/destinations.json';

/** Query param carrying the JSON-encoded refs on a cold-start notification tap. */
export const NREF_PARAM = 'nref';

/**
 * Registry ref-name allowlist per type: exactly the refs the page-side
 * resolver consumes for that type. Anything else a push payload carries
 * (message_content, title, body, ...) must NOT ride the cold-start URL. Built
 * from the same vendored registry the resolver reads, so the two stay in sync.
 */
const DECLARED_REFS_BY_TYPE: Record<string, readonly string[]> = Object.fromEntries(
  Object.entries(destinationsRegistry.destinations).map(([type, entry]) => [type, entry.refs]),
);

/**
 * Prune flattened refs to the fields the resolver reads: `type` plus the
 * registry-declared ref names for that type. Keeps private payload scalars
 * (chat message text, notification titles/bodies) out of the
 * /notifications?nref= URL, the address bar, and browser history - and caps
 * the URL length so a long message can't break openWindow.
 *
 * Own-property lookup (hasOwnProperty) so a payload-controlled type like
 * 'constructor' can't read a ref list off Object.prototype - mirrors the
 * resolver's own lookupDestination guard.
 */
function pruneRefsForUrl(refs: PushNotificationRefs): PushNotificationRefs {
  const pruned: PushNotificationRefs = {};
  const { type } = refs;
  if (type === undefined) return pruned;
  pruned.type = type;
  // N3 "honest bell": notification_id rides the cold-start URL for EVERY type
  // (independent of the per-type declared refs) so a cold-start tap can POST
  // /notifications/{id}/click. It is an opaque server id, not private payload
  // content, so it is URL-safe. Kept alongside `type` in the always-kept set.
  const notificationId = refs.notification_id;
  if (notificationId !== undefined) pruned.notification_id = notificationId;
  const declared = Object.prototype.hasOwnProperty.call(DECLARED_REFS_BY_TYPE, type)
    ? DECLARED_REFS_BY_TYPE[type]
    : undefined;
  if (declared) {
    for (const name of declared) {
      const value = refs[name];
      if (value !== undefined) pruned[name] = value;
    }
  }
  return pruned;
}

/**
 * Flattened notification context scalars keyed by ref name. Structurally
 * identical to the resolver's NotificationRefs (Record<string, string |
 * undefined>) - redeclared here because nothing in the SW bundle may import
 * from the resolver module. The undefined in the index type is deliberate:
 * no key (not even 'type') is guaranteed present.
 */
export type PushNotificationRefs = Record<string, string | undefined>;

/** Structural subset of WindowClient the picker reads (keeps this module
 *  compilable and testable outside the WebWorker lib). */
export interface FocusClientCandidate {
  focused?: boolean;
  visibilityState?: string;
}

/**
 * Pick the window client a warm notification tap should land on: the focused
 * client first, else the first visible one, else the first entry.
 *
 * Fixes W5: the old handler posted to whichever same-origin client happened
 * to be first in matchAll order, which is often a background tab - the
 * navigation then ran in a window the user was not looking at.
 */
export function pickFocusClient<T extends FocusClientCandidate>(
  clients: readonly T[],
): T | undefined {
  return (
    clients.find(client => client.focused === true) ??
    clients.find(client => client.visibilityState === 'visible') ??
    clients[0]
  );
}

/**
 * Narrow an untrusted candidate (a postMessage `refs` field, or JSON parsed
 * from the nref param) to flattened refs: a plain object reduced to its
 * scalar string entries. Returns null when the candidate is not a plain
 * object - callers treat that as "no refs" and stay on their legacy path.
 */
export function sanitizeRefs(candidate: unknown): PushNotificationRefs | null {
  if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
    return null;
  }
  const refs: PushNotificationRefs = {};
  for (const [key, value] of Object.entries(candidate)) {
    if (typeof value === 'string') {
      refs[key] = value;
    }
  }
  return refs;
}

/**
 * Extract destination refs from a push payload's `data`:
 * `{ type: data.type, ...every scalar string entry of data }`.
 *
 * Returns null when `data.type` is missing or not a non-empty string - that
 * is a legacy (pre-contract) payload and the caller must keep the existing
 * url-only behavior verbatim (dual-read transition).
 */
export function extractRefs(
  data: Record<string, unknown> | undefined,
): PushNotificationRefs | null {
  const refs = sanitizeRefs(data);
  return refs && refs.type ? refs : null;
}

/**
 * Cold-start destination URL: the notifications hub with the refs riding a
 * ?nref= JSON param. The page-side useNrefBootResolve hook consumes the param
 * after boot and resolves the real destination in-page - including across an
 * auth round-trip, since storeRedirectPath preserves path + search through
 * login.
 *
 * Refs are pruned to `{ type }` + the registry-declared refs for that type
 * (pruneRefsForUrl) before encoding, so private payload scalars never reach
 * the address bar or history.
 *
 * '/notifications' stays a literal here: importing the routes factory would
 * pull app modules into the service worker bundle.
 */
export function buildNrefUrlFromRefs(refs: PushNotificationRefs): string {
  const pruned = pruneRefsForUrl(refs);
  return `/notifications?${NREF_PARAM}=${encodeURIComponent(JSON.stringify(pruned))}`;
}
